import { useEffect } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { Navigate, useSearch } from '@tanstack/react-router';
import { homeRoute } from '@/lib/tier';

/**
 * Hands off to Kinde's hosted sign-in page.
 *
 * This route renders no form of its own. We build no sign-in UI, exactly as the current
 * product does not — `login()` with no `connectionId` redirects to Kinde, which presents the
 * emailed code and all six social providers on its own screen.
 *
 * ## Why not our own screen
 *
 * A first version of this rendered a custom form with a button per provider, on the reasoning
 * that the shared application has "Use your own sign-up and sign-in screens" enabled. That
 * setting permits custom screens; it does not require them, and the current product proves it
 * by using Kinde's hosted page through the same application. Hosted screens are the better
 * default here for reasons that outlast the preference:
 *
 *   - **One place to change a sign-in method.** Enabling a seventh provider in Kinde makes it
 *     appear here with no deploy. A hand-built list silently omits it, which does not look
 *     broken — it looks like that provider was never offered, and the user who signed up with
 *     it simply cannot get in.
 *   - **The emailed code screen is Kinde's regardless.** A custom first screen only adds a step
 *     before handing off to Kinde anyway.
 *   - **Nothing sensitive touches our code.** No credential, no code entry, no rate limiting or
 *     enumeration defence for us to get wrong.
 *
 * The consequence to accept: sign-in is branded at `auth.awarenow.ai`, not by this app. Kinde's
 * own branding settings control that, and they are shared with the current product — which is
 * the desired outcome here, since the two should look like one company.
 */
export function SignInRoute() {
  const { isAuthenticated, isLoading, login, register } = useKindeAuth();
  const search = useSearch({ strict: false }) as { returnTo?: string; intent?: string };

  const authenticated = !isLoading && isAuthenticated;

  useEffect(() => {
    if (isLoading || isAuthenticated) return;

    // Only same-origin paths. A `returnTo` taken straight from the query string is an open
    // redirect: an attacker sends `?returnTo=https://evil.example`, the user signs in for
    // real, and we hand them to a lookalike page with their guard fully down.
    const returnTo = search.returnTo;
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      sessionStorage.setItem('aware.returnTo', returnTo);
    }

    // `register` and `login` land on the same Kinde screens; the difference is which tab opens
    // first. Passwordless makes them functionally identical — the same address either matches
    // an account or creates one — so this only shapes the first impression.
    void (search.intent === 'up' ? register() : login());
  }, [isLoading, isAuthenticated, login, register, search.intent, search.returnTo]);

  // Already signed in — never show a sign-in step to someone who has one.
  if (authenticated) return <Navigate to={homeRoute()} replace />;

  return (
    <div className="signin-handoff" role="status" aria-live="polite">
      <p>Taking you to sign in…</p>
    </div>
  );
}
